import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form",
    "previewFrame",
    "previewFrameDesktop",
    "stepCard",
    "stepSettings",
    "stepsList",
    "stepContent",
    "stepNumber",
    "deviceBtn",
    "mobileFrame",
    "desktopFrame",
    "deviceToggle",
    "flowOption",
    "flowRadio",
    "flowTypeSelector"
  ]
  static values = {
    contractor: String,
    previewBaseUrl: String,
    demo: { type: Boolean, default: true }
  }

  connect() {
    this.currentStep = "address"
    this.currentDevice = "mobile"
    this.updateFlowList()
  }

  // Toggle between mobile and desktop preview modes
  setDeviceMode(event) {
    const device = event.currentTarget.dataset.device
    this.currentDevice = device

    // Update button states
    this.deviceBtnTargets.forEach(btn => {
      if (btn.dataset.device === device) {
        btn.classList.add("bg-white", "dark:bg-gray-700", "text-gray-900", "dark:text-gray-100", "shadow-sm")
        btn.classList.remove("text-gray-500", "dark:text-gray-400", "hover:text-gray-700", "dark:hover:text-gray-300")
      } else {
        btn.classList.remove("bg-white", "dark:bg-gray-700", "text-gray-900", "dark:text-gray-100", "shadow-sm")
        btn.classList.add("text-gray-500", "dark:text-gray-400", "hover:text-gray-700", "dark:hover:text-gray-300")
      }
    })

    // Toggle frame visibility
    if (device === "mobile") {
      this.mobileFrameTarget.classList.remove("hidden")
      this.desktopFrameTarget.classList.add("hidden")
    } else {
      this.mobileFrameTarget.classList.add("hidden")
      this.desktopFrameTarget.classList.remove("hidden")
    }

    // Sync the current step to the active frame
    this.navigatePreview(this.currentStep)
  }

  // Navigate to a specific step in the preview
  selectStep(event) {
    const card = event.currentTarget.closest("[data-step]")
    const step = card.dataset.step

    // If the card is expandable and has settings, toggle expansion
    if (card.dataset.expandable === "true") {
      this.toggleExpansion(card)
    }

    // Navigate preview to this step
    this.navigatePreview(step)

    // Highlight active step card
    this.highlightStep(step)
  }

  // Toggle step expansion for cards with settings
  toggleExpansion(card) {
    const stepKey = card.querySelector("[data-step]")?.dataset.step || card.dataset.step
    const settings = this.stepSettingsTargets.find(s => s.dataset.step === stepKey.replace("_step", ""))

    if (!settings) return

    const isExpanded = card.dataset.expanded === "true"

    if (isExpanded) {
      settings.classList.add("hidden")
      card.dataset.expanded = "false"
    } else {
      settings.classList.remove("hidden")
      card.dataset.expanded = "true"
    }
  }

  // Toggle settings panel from cog button
  toggleSettings(event) {
    event.stopPropagation()
    const card = event.currentTarget.closest("[data-checkout-flow-target='stepCard']")
    if (card) {
      this.toggleExpansion(card)
    }
  }

  // Navigate preview iframe to a step
  navigateToStep(event) {
    const step = event.currentTarget.dataset.step
    this.navigatePreview(step)
    this.highlightStep(step)
  }

  navigatePreview(step) {
    this.currentStep = step

    // Build the preview URL based on step
    const stepPaths = {
      "address": "",
      "pool_verification": "/pool_verification",
      "services": "/services",
      "upsells": "/upsells",
      "contact": "/contact",
      "agreement": "/agreement",
      "checkout": "/checkout",
      "success": "/success"
    }

    const path = stepPaths[step] || ""
    const modeParam = this.demoValue ? "demo=true" : "preview=true"
    const previewUrl = `${this.previewBaseUrlValue}${path}?${modeParam}`

    // Update both iframes to keep them in sync
    if (this.hasPreviewFrameTarget) {
      this.previewFrameTarget.src = previewUrl
    }

    if (this.hasPreviewFrameDesktopTarget) {
      this.previewFrameDesktopTarget.src = previewUrl
    }
  }

  highlightStep(step) {
    // Highlight step card
    this.stepCardTargets.forEach(card => {
      if (card.dataset.step === step) {
        card.classList.add("bg-primary-50", "dark:bg-primary-900/20")
      } else {
        card.classList.remove("bg-primary-50", "dark:bg-primary-900/20")
      }
    })
  }

  // Handle flow type change (Standard vs Quote Only)
  updateFlowType(event) {
    const isQuoteOnly = event.target.value === "true"

    // Update flow option visual states
    this.flowOptionTargets.forEach(option => {
      const flowType = option.dataset.flowType
      const isSelected = (flowType === "quote" && isQuoteOnly) || (flowType === "standard" && !isQuoteOnly)
      const checkmark = option.querySelector(".flow-check")

      // Update border and background
      if (isSelected) {
        option.classList.add("border-brand-500", "bg-brand-50", "dark:bg-brand-900/20")
        option.classList.remove("border-gray-200", "dark:border-gray-700", "hover:border-gray-300")
        if (checkmark) checkmark.classList.remove("hidden")
      } else {
        option.classList.remove("border-brand-500", "bg-brand-50", "dark:bg-brand-900/20")
        option.classList.add("border-gray-200", "dark:border-gray-700", "hover:border-gray-300")
        if (checkmark) checkmark.classList.add("hidden")
      }
    })

    // Update UI to show/hide steps based on flow type
    this.stepCardTargets.forEach(card => {
      const stepKey = card.dataset.step

      // Hide services and payment steps in quote-only mode
      if (isQuoteOnly && (stepKey === "services" || stepKey === "checkout")) {
        card.classList.add("hidden")
      } else {
        card.classList.remove("hidden")
      }
    })

    // Update flow list
    this.updateFlowList()

    // Refresh the preview
    this.navigatePreview("address")
  }

  // Handle step toggle (enable/disable optional steps)
  toggleStep(event) {
    const stepKey = event.currentTarget.dataset.step
    const isEnabled = event.currentTarget.checked

    // Find the settings panel for this step and show/hide
    const settings = this.stepSettingsTargets.find(s => s.dataset.step === stepKey)
    const card = event.currentTarget.closest(".checkout-step-card")
    const content = card?.querySelector("[data-checkout-flow-target='stepContent']")
    const stepNumberBtn = card?.querySelector("[data-checkout-flow-target='stepNumber']")
    const numberEl = card?.querySelector(".flow-step-number")
    const hiddenIcon = card?.querySelector(".step-hidden-icon")

    if (card) {
      card.dataset.enabled = isEnabled

      // Update opacity on the content div
      if (content) {
        if (isEnabled) {
          content.classList.remove("opacity-50")
        } else {
          content.classList.add("opacity-50")
        }
      }

      // Toggle between number and eye-slash icon
      if (stepNumberBtn) {
        if (isEnabled) {
          stepNumberBtn.classList.remove("bg-gray-100", "dark:bg-gray-700")
          stepNumberBtn.classList.add("bg-brand-100", "dark:bg-brand-900/30", "hover:bg-brand-200", "dark:hover:bg-brand-800/40")
          if (numberEl) numberEl.classList.remove("hidden")
          if (hiddenIcon) hiddenIcon.classList.add("hidden")
        } else {
          stepNumberBtn.classList.add("bg-gray-100", "dark:bg-gray-700")
          stepNumberBtn.classList.remove("bg-brand-100", "dark:bg-brand-900/30", "hover:bg-brand-200", "dark:hover:bg-brand-800/40")
          if (numberEl) numberEl.classList.add("hidden")
          if (hiddenIcon) hiddenIcon.classList.remove("hidden")
        }
      }

      if (settings) {
        if (isEnabled) {
          // Allow expansion
          card.dataset.expandable = "true"
        } else {
          // Collapse and disable expansion
          settings.classList.add("hidden")
          card.dataset.expanded = "false"
          card.dataset.expandable = "false"
        }
      }
    }

    // Update step numbers
    this.updateFlowList()
  }

  // Update step card numbers based on visibility
  updateFlowList() {
    let visibleNumber = 0

    this.stepCardTargets.forEach(card => {
      const isHidden = card.classList.contains("hidden")
      const isDisabled = card.dataset.enabled === "false"
      const numberEl = card.querySelector(".flow-step-number")

      if (!isHidden && !isDisabled) {
        visibleNumber++
        if (numberEl) {
          numberEl.textContent = visibleNumber
        }
      }
    })
  }
}
